/* ============================================================
   BB SITE ANIMATIONS — global GSAP layer for all bb-* sections
   Content is never hidden by CSS: every animation is gsap.from,
   so if JS fails the site renders exactly as before.
   ============================================================ */
(function () {
  "use strict";

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  ready(function () {
    if (!window.gsap || !window.ScrollTrigger) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    gsap.registerPlugin(ScrollTrigger);

    var EASE = "power3.out";

    /* Pick only elements not yet animated (safe for shopify:section:load re-runs)
       and never touch the Saccades section — it has its own timeline. */
    function fresh(scope, selector) {
      return gsap.utils.toArray(scope.querySelectorAll(selector)).filter(function (el) {
        if (el.dataset.bbAnimated || el.closest(".bb-sacc")) return false;
        el.dataset.bbAnimated = "1";
        return true;
      });
    }

    function revealUp(els, opts) {
      els.forEach(function (el) {
        gsap.from(el, Object.assign({
          autoAlpha: 0, y: 36, duration: 0.7, ease: EASE,
          scrollTrigger: { trigger: el, start: "top 82%", once: true }
        }, opts || {}));
      });
    }

    /* Stagger a group of cards as one batch per grid.
       Fade only — these cards have a CSS hover transition on transform, and
       animating transform with GSAP fights it (slow-motion, stuck-faded cards).
       clearProps removes leftover inline styles so hovers stay crisp. */
    function staggerCards(els) {
      if (!els.length) return;
      ScrollTrigger.batch(els, {
        start: "top 88%",
        once: true,
        onEnter: function (batch) {
          gsap.from(batch, { autoAlpha: 0, duration: 0.5, stagger: 0.08, ease: EASE, clearProps: "opacity,visibility" });
        }
      });
    }

    function initScope(scope) {

      /* ---------- HERO SPLIT: panels settle, content cascades, connector pops ---------- */
      fresh(scope, ".bb-hero-split").forEach(function (hero) {
        var tl = gsap.timeline({ delay: 0.1 });
        var bgs = hero.querySelectorAll(".bb-hero-split__bg");
        if (bgs.length) tl.from(bgs, { scale: 1.1, duration: 1.6, ease: "power2.out" }, 0);
        hero.querySelectorAll(".bb-hero-split__content").forEach(function (content, i) {
          tl.from(content.children, {
            autoAlpha: 0, y: 34, duration: 0.7, stagger: 0.09, ease: EASE
          }, 0.15 + i * 0.15);
        });
        var conn = hero.querySelector(".bb-hero-split__connector-circle");
        if (conn) tl.from(conn, { scale: 0, rotation: -90, duration: 0.6, ease: "back.out(1.8)" }, 0.7);
        var strip = hero.querySelector(".bb-hero-split__strip, .bb-hero-split__connector-text");
        if (strip) tl.from(strip, { autoAlpha: 0, y: 20, duration: 0.5, ease: EASE }, 0.85);
      });

      /* ---------- TEXT INTRO: lines draw, text cascades, stats pop ---------- */
      fresh(scope, ".bb-intro").forEach(function (sec) {
        var tl = gsap.timeline({ scrollTrigger: { trigger: sec, start: "top 75%", once: true } });
        var lines = sec.querySelectorAll(".bb-intro__line");
        if (lines.length) tl.from(lines, { scaleX: 0, duration: 0.7, ease: "power2.inOut" }, 0);
        var eyebrow = sec.querySelector(".bb-intro__eyebrow");
        if (eyebrow) tl.from(eyebrow, { autoAlpha: 0, y: 14, duration: 0.5, ease: EASE }, 0.15);
        var text = sec.querySelectorAll(".bb-intro__title, .bb-intro__desc, .bb-intro__body");
        if (text.length) tl.from(text, { autoAlpha: 0, y: 30, duration: 0.7, stagger: 0.12, ease: EASE }, 0.3);
        var stats = sec.querySelectorAll(".bb-intro__stat");
        if (stats.length) tl.from(stats, { autoAlpha: 0, y: 24, scale: 0.9, duration: 0.5, stagger: 0.1, ease: "back.out(1.6)" }, 0.55);
      });

      /* ---------- PRODUCT SHOWCASE: image slides from its side, text cascades ---------- */
      fresh(scope, ".bb-showcase__row").forEach(function (row) {
        var reversed = row.classList.contains("bb-showcase__row--reverse");
        var imgCol = row.querySelector(".bb-showcase__image-col");
        var img = row.querySelector(".bb-showcase__img");
        var pills = row.querySelectorAll(".bb-showcase__pill");
        var textBits = row.querySelectorAll(
          ".bb-showcase__label, .bb-showcase__title, .bb-showcase__desc, .bb-showcase__features li, .bb-showcase__subtext, .bb-showcase__btn"
        );

        /* img itself is left to CSS (it has its own hover zoom + transition);
           pills have a CSS float animation that owns transform, so fade only;
           clearProps removes leftover inline styles so CSS hovers keep working */
        var tl = gsap.timeline({ scrollTrigger: { trigger: row, start: "top 72%", once: true } });
        if (imgCol) tl.from(imgCol, { autoAlpha: 0, x: reversed ? 70 : -70, duration: 0.8, ease: EASE, clearProps: "transform,opacity,visibility" }, 0);
        if (pills.length) tl.from(pills, { autoAlpha: 0, duration: 0.5, stagger: 0.12, ease: EASE, clearProps: "opacity,visibility" }, 0.45);
        if (textBits.length) tl.from(textBits, { autoAlpha: 0, y: 26, duration: 0.55, stagger: 0.07, ease: EASE, clearProps: "transform,opacity,visibility" }, 0.25);
      });

      /* ---------- VECTOR BALL COLOR FLASH: green -> white every 3s
         (mimics the ball's impact-activated color flash) ---------- */
      fresh(scope, ".bb-showcase__img[src*='bb-vb-green']").forEach(function (img) {
        gsap.fromTo(img,
          {
            filter: "drop-shadow(0 20px 50px rgba(0,0,0,0.4)) drop-shadow(0 0 36px rgba(3,118,58,0.45)) saturate(1) brightness(1)"
          },
          {
            filter: "drop-shadow(0 20px 50px rgba(0,0,0,0.4)) drop-shadow(0 0 36px rgba(255,255,255,0.55)) saturate(0) brightness(1.5)",
            duration: 0.5,
            ease: "power2.inOut",
            repeat: -1,
            yoyo: true,
            repeatDelay: 3
          });
      });

      /* NOTE: bb-features-grid, bb-how-it-works and bb-social-proof have their
         own IntersectionObserver entrance animations — the GSAP layer must NOT
         touch them (CSS animations override inline transforms => double/glitchy). */

      /* ---------- BUNDLE: products converge on the plus ---------- */
      fresh(scope, ".bb-bundle__card").forEach(function (card) {
        var tl = gsap.timeline({ scrollTrigger: { trigger: card, start: "top 75%", once: true } });
        tl.from(card, { autoAlpha: 0, y: 44, duration: 0.7, ease: EASE }, 0);
        var products = card.querySelectorAll(".bb-bundle__product");
        if (products.length === 2) {
          tl.from(products[0], { x: -46, autoAlpha: 0, duration: 0.65, ease: EASE }, 0.25)
            .from(products[1], { x: 46, autoAlpha: 0, duration: 0.65, ease: EASE }, 0.25);
        }
        var plus = card.querySelector(".bb-bundle__plus-wrap, .bb-bundle__plus");
        if (plus) tl.from(plus, { scale: 0, rotation: 180, duration: 0.55, ease: "back.out(1.8)" }, 0.45);
        var cta = card.querySelector(".bb-bundle__cta");
        if (cta) tl.from(cta, { autoAlpha: 0, y: 20, duration: 0.5, ease: EASE }, 0.6);
      });
      revealUp(fresh(scope, ".bb-bundle__header"));

      /* ---------- CARD GRIDS: products, drills, news (trust animates itself) ---------- */
      staggerCards(fresh(scope, ".bb-product-card"));
      staggerCards(fresh(scope, ".bb-drill-lib__card"));
      staggerCards(fresh(scope, ".bb-news__card"));

      /* ---------- Generic headers / footers of remaining sections ---------- */
      revealUp(fresh(scope, ".bb-news__header, .bb-drill-lib__header, .bb-drill-lib__filters, .bb-news__footer"));

      /* ---------- CTA: halves slide toward each other.
         One timeline per SECTION ROOT — .bb-cta__inner contains .bb-cta__grid,
         so selecting both created two competing timelines on the same elements
         (auto-overwrite killed the first mid-tween => content stuck faded). ---------- */
      fresh(scope, ".bb-cta").forEach(function (sec) {
        var left = sec.querySelector(".bb-cta__left");
        var right = sec.querySelector(".bb-cta__right");
        var tl = gsap.timeline({ scrollTrigger: { trigger: sec, start: "top 82%", once: true } });
        if (left) tl.from(left, { autoAlpha: 0, x: -44, duration: 0.7, ease: EASE, clearProps: "transform,opacity,visibility" }, 0);
        if (right) tl.from(right, { autoAlpha: 0, x: 44, duration: 0.7, ease: EASE, clearProps: "transform,opacity,visibility" }, 0.1);
      });
    }

    /* ---------- Scroll progress bar (brand gradient, top of viewport) ---------- */
    (function () {
      if (document.querySelector(".bb-scroll-progress")) return;
      var bar = document.createElement("div");
      bar.className = "bb-scroll-progress";
      bar.setAttribute("aria-hidden", "true");
      document.body.appendChild(bar);
      gsap.to(bar, {
        scaleX: 1, ease: "none",
        scrollTrigger: { trigger: document.documentElement, start: "top top", end: "bottom bottom", scrub: 0.3 }
      });
    })();

    initScope(document);

    /* Late layout shifts (images, fonts, app embeds) can leave trigger
       positions stale and reveals unfired — re-measure once everything
       has loaded, and once more after apps have settled. */
    window.addEventListener("load", function () { ScrollTrigger.refresh(); });
    setTimeout(function () { ScrollTrigger.refresh(); }, 3500);

    /* Theme editor: animate re-rendered sections without a reload */
    document.addEventListener("shopify:section:load", function (e) {
      initScope(e.target);
      ScrollTrigger.refresh();
    });
  });
})();
